import chalk from 'chalk';
import * as stackTrace from 'stack-trace';

// These "FIXTAG" lines are automatically uncommented for Node 18 and above
// in a postinstall script when used in a "module" context
// First added for puppeteer-chatter, see the package.json script 'postinstall:fix-logger' there
// FIXTAG //import * as url from 'url';
// FIXTAG //global.__filename = url.fileURLToPath(import.meta.url);

const chalkOrange = chalk.hex('#FFA500');
chalk.orange = chalkOrange;

export { chalk };

/**
 * Enumeration of available log levels
 * @readonly
 * @enum {string}
 */
export const LogLevel = {
	/** Information level logging */
	Info: 'INFO',
	/** Debug level logging */
	Debug: 'DEBUG',
	/** Warning level logging */
	Warn: 'WARN',
	/** Error level logging */
	Error: 'ERROR',
};

/**
 * Extracts the basename from a file path
 * @param {string} path - The file path to extract basename from
 * @returns {string} The basename of the path
 */
const basename = (path) => path.split(/[\\/]/).pop();

/**
 * Validates if a given level is a valid log level
 * @param {string} level - The level to validate
 * @returns {boolean} True if the level is valid, false otherwise
 */
export const isValidLogLevel = (level) =>
	Object.values(LogLevel).includes(level);

/**
 * Stack trace utility for use by AuditLog in the backend
 * @type {typeof import('stack-trace')}
 */
export const StackTraceUtil = stackTrace;

/**
 * Gets the stack trace for the current function
 * @returns {Object} Object with stack property containing the stack trace
 */
export function getStack() {
	const obj = {};
	Error.captureStackTrace(obj, getStack);
	return obj;
}

const PARENT_MATCH_REGEX = /\(([^)]+)\)/;
const DIRECT_MATCH_REGEX = /at\s+([^()\s]+:\d+:\d+)/;

const PATCH_MATCH_REGEX =
	/(?:\/src\/(services\/[^/]+)\/|\/(managed-apps\/[^/]+)\/)/;
const INDEX_MATCH_REGEX = /\/index\.js$/;

/**
 * Options for getCallerLocation function
 * @typedef {Object} CallerLocationOptions
 * @property {string[]} [excludes=[]] - Additional paths to exclude from stack trace
 */

/**
 * Information about the caller location
 * @typedef {Object} CallerLocationInfo
 * @property {string} file - The filename where the call originated
 * @property {number} line - The line number where the call originated
 * @property {string} service - The service name extracted from the filepath
 * @property {string} filepath - The full filepath where the call originated
 */

/**
 * Gets the location (file and line) where a log call originated, excluding logger internals
 * @param {CallerLocationOptions} [options={}] - Options for filtering the stack trace
 * @returns {CallerLocationInfo} Information about the caller location
 */
export function getCallerLocation({
	excludes = [],
	useCaptureStackTrace = false,
} = {}) {
	const loggerExcludes = [
		'/Logger.js',
		'node:internal/',
		'/node_modules/tsx/',
		'/node_modules/',
		...excludes,
	];

	let filepath;
	let line;

	// First, try native Error stack (respects source maps)
	const error = useCaptureStackTrace ? getStack() : new Error('stack trace');
	if (error.stack) {
		const stackLines = error.stack.split('\n');
		const callerLine = stackLines.find((stackLine, index) => {
			if (index === 0) return false; // Skip error message line
			const trimmed = stackLine.trim();
			if (!trimmed.startsWith('at')) return false;

			// Exclude this function itself and logger internals
			if (stackLine.includes('getCallerLocation')) return false;

			const shouldExclude = loggerExcludes.some((exclude) =>
				stackLine.includes(exclude),
			);
			return !shouldExclude;
		});

		if (callerLine) {
			let fileInfo = null;
			// Try to extract file info from different stack trace formats
			const parenMatch = callerLine.match(PARENT_MATCH_REGEX);
			const directMatch = callerLine.match(DIRECT_MATCH_REGEX);

			if (parenMatch) {
				[, fileInfo] = parenMatch;
			} else if (directMatch) {
				[, fileInfo] = directMatch;
			}

			if (fileInfo && fileInfo.includes(':')) {
				const parts = fileInfo.split(':');
				if (parts.length >= 2) {
					const lineNum = parseInt(parts[parts.length - 2], 10);
					if (Number.isInteger(lineNum)) {
						filepath = parts.slice(0, -2).join(':');
						line = lineNum;
					}
				}
			}
		}
	}

	// Fallback to stack-trace module if native parsing failed
	if (!filepath || !line) {
		let trace = stackTrace.get();
		if (Array.isArray(trace) && trace.length) {
			const caller = trace.find(
				(data) =>
					data &&
					typeof data.getFileName === 'function' &&
					data.getFileName() &&
					!loggerExcludes.some((exclude) =>
						data.getFileName().includes(exclude),
					),
			);
			if (caller) {
				filepath = caller.getFileName();
				line = caller.getLineNumber();
			}
		}

		// Final fallback to string parsing
		if (!filepath && typeof trace !== 'string') {
			trace = new Error('stack trace').stack;
		}

		if (typeof trace === 'string') {
			const list = trace.split('\n');
			const match =
				list.find(
					(test) =>
						test.trim().startsWith('at') &&
						!loggerExcludes.some((exclude) => test.includes(exclude)),
				) || '<unknown>:<unknown>';

			const idx1 = match.indexOf('(');
			const idx2 = match.indexOf(')');
			if (idx1 !== -1 && idx2 !== -1) {
				const fileAndLine = match.substring(idx1 + 1, idx2 - 1);
				[filepath, line] = fileAndLine.split(':');
			}
		}
	}

	// For ESM modules, stacks may have 'file://' prefixed on the actual path, and basename doesn't parse that
	filepath = `${filepath || ''}`.replace('file://', '');

	let file = basename(filepath || '');

	// index.js is not helpful, so replace with containing folder name
	if (file === 'index.js' && filepath) {
		file = basename(filepath.replace(INDEX_MATCH_REGEX, ''));
	}

	// Attempt to extract the service name from the filepath
	const pathMatch = filepath ? filepath.match(PATCH_MATCH_REGEX) : null;
	const service = pathMatch ? pathMatch[1] || pathMatch[2] : '';

	return { file, line, service, filepath };
}

/**
 * Safe logging function that protects against runtime problems in older Node versions
 * @param {string} method - The console method to use ('log', 'error', 'warn', 'debug')
 * @param {...any} args - Arguments to pass to the console method
 * @returns {void}
 */
const failedMethods = {};
export const safeLog = (method, ...args) => {
	if (method === 'debug') {
		// eslint-disable-next-line no-param-reassign
		method = 'info'; // debug method does not exist in node 8.11
	}

	// eslint-disable-next-line no-console
	const fn = console[method];
	if (typeof fn === 'function') {
		// eslint-disable-next-line no-console
		fn.apply(console, args);
	} else {
		if (!failedMethods[method]) {
			failedMethods[method] = true;
			// eslint-disable-next-line no-console
			console.log('***** INTERNAL ERROR *****');
			// eslint-disable-next-line no-console
			console.log(
				`***** Logger tried to call method '${method}' on console, but that didn't exist, falling back to console.log...`,
			);
			// eslint-disable-next-line no-console
			console.log('***** INTERNAL ERROR *****');
		}

		// eslint-disable-next-line no-console
		console.log(...args);
	}
};

/**
 * Filter callback function type for intercepting log messages
 * @callback FilterCallback
 * @param {LogLevelInput} levelInput - The log level input
 * @param {string} codeLocation - The code location where the log originated
 * @param {number} pid - The process ID
 * @param {...any} args - The log arguments
 * @returns {boolean} True to intercept (block) the log message, false to allow it through
 */

/**
 * OnLog callback function type for custom log handling
 * @callback OnLogCallback
 * @param {string} level - The log level
 * @param {string} codeLocation - The code location where the log originated
 * @param {...any} args - The log arguments
 * @param {Object} meta - Metadata object containing __loggerMeta
 * @returns {boolean} True to intercept (block) the log message, false to allow it through
 */

/**
 * Log level input type - can be a string or an object with additional properties
 * @typedef {string|LogLevelObject} LogLevelInput
 */

/**
 * Log level object with additional properties
 * @typedef {Object} LogLevelObject
 * @property {string} type - The log level type
 * @property {string} [customPrefix] - Custom prefix for the log message
 * @property {string} [codeLocationOverride] - Override for the code location
 * @property {Object} [customContextProps] - Custom context properties
 * @property {CustomLogger} [customLogger] - Custom logger instance
 * @property {OnLogCallback} [onLogOverride] - Override for onLog callback
 */

/**
 * Metadata for log levels including color, method, and other properties
 * @typedef {Object} LogLevelMeta
 * @property {string} level - The log level
 * @property {string} method - The console method to use
 * @property {string} color - The chalk color to use
 * @property {string} [customPrefix] - Custom prefix for the log message
 * @property {string} [codeLocationOverride] - Override for the code location
 * @property {Object} [customContextProps] - Custom context properties
 * @property {CustomLogger} [customLogger] - Custom logger instance
 * @property {OnLogCallback} [onLogOverride] - Override for onLog callback
 */

/**
 * Custom logger instance with prefixed logging methods
 * @typedef {Object} CustomLogger
 * @property {Function} log - Log method with custom level support
 * @property {Function} error - Error logging method
 * @property {Function} warn - Warning logging method
 * @property {Function} info - Info logging method
 * @property {Function} debug - Debug logging method
 * @property {string} customPrefix - The custom prefix for this logger
 * @property {Object} customContextProps - Custom context properties
 * @property {OnLogCallback} [onLogOverride] - Override for onLog callback
 * @property {Function} auditLog - Audit logging function
 * @property {Object} [authorization] - Authorization context
 * @property {Function} getPrefixedCustomLogger - Method to create sub-loggers
 * @property {Function} alert - Alert method for critical notifications
 */

/**
 * Union type for logger parameters - can be either the Logger class or a CustomLogger instance
 * @typedef {typeof Logger|CustomLogger} LoggerParam
 */

/**
 * Audit log function type
 * @callback AuditLogFunction
 * @param {string} actionId - Unique identifier for the action
 * @param {string} action - Description of the action
 * @param {string} comments - Additional comments about the action
 * @returns {Object} Object with id property containing the audit log ID
 */

/**
 * Alert handler function type
 * @callback AlertHandler
 * @param {string} label - The alert label
 * @param {...any} args - Additional alert arguments
 * @param {Object} [context] - Context object with logger, authorization, etc.
 * @returns {boolean} True if the alert was handled, false otherwise
 */

/**
 * Main Logger class providing structured logging functionality with filtering,
 * custom prefixes, and audit capabilities
 */
// Use globalThis for static state to survive ESM module duplication
const LOGGER_GLOBAL_KEY = '__RUBBER_LOGGER__';
if (!globalThis[LOGGER_GLOBAL_KEY]) {
	globalThis[LOGGER_GLOBAL_KEY] = {
		filterCallbacks: [],
		onAlert: undefined,
		onLog: undefined,
		auditLog: undefined,
	};
}
const loggerGlobalState = globalThis[LOGGER_GLOBAL_KEY];

class Logger {
	/**
	 * Array of filter callbacks that can intercept log messages
	 * @type {FilterCallback[]}
	 * @static
	 */
	static get filterCallbacks() {
		return loggerGlobalState.filterCallbacks;
	}

	static set filterCallbacks(value) {
		loggerGlobalState.filterCallbacks = value;
	}

	/**
	 * Alert handler function for critical notifications
	 * @type {AlertHandler}
	 * @static
	 */
	static get onAlert() {
		return loggerGlobalState.onAlert;
	}

	static set onAlert(value) {
		loggerGlobalState.onAlert = value;
	}

	/**
	 * OnLog handler for custom log processing
	 * @type {OnLogCallback}
	 * @static
	 */
	static get onLog() {
		return loggerGlobalState.onLog;
	}

	static set onLog(value) {
		loggerGlobalState.onLog = value;
	}

	// /**
	//  * Audit log function for the Logger class
	//  * @type {AuditLogFunction}
	//  * @static
	//  */
	// static get auditLog() {
	// 	return loggerGlobalState.auditLog;
	// }

	// static set auditLog(value) {
	// 	loggerGlobalState.auditLog = value;
	// }

	/**
	 * Authorization context for the Logger class
	 * @type {Object}
	 * @static
	 */
	static authorization;

	/**
	 * Custom context properties for the Logger class
	 * @type {Object}
	 * @static
	 */
	static customContextProps;

	/**
	 * @deprecated Use addFilter() instead. Sets a single filter callback (legacy method)
	 * @param {FilterCallback} filterCallback - The filter callback function
	 * @static
	 */
	static setFilter(filterCallback) {
		this.warn(
			`You really should now be calling Logger.addFilter() instead of Logger.setFilter(), we'll fix that for you now, but go updated code in this stack:`,
			new Error('called from').stack,
		);
		this.addFilter(filterCallback);
	}

	/**
	 * Adds a filter callback that can intercept log messages before they're output
	 * @param {FilterCallback} filterCallback - The filter callback function that returns true to block the message
	 * @returns {Object} Object with unsubscribe method to remove the filter
	 * @static
	 */
	static addFilter(filterCallback) {
		this.filterCallbacks.push(filterCallback);

		const unsubscribe = () => {
			this.removeFilter(filterCallback);
		};

		return { unsubscribe };
	}

	/**
	 * Removes a filter callback from the filter list
	 * @param {FilterCallback} filterCallback - The filter callback to remove
	 * @static
	 */
	static removeFilter(filterCallback) {
		this.filterCallbacks = this.filterCallbacks.filter(
			(cb) => cb !== filterCallback,
		);
	}

	/**
	 * Main logging method that handles all log output with filtering and formatting
	 * @param {LogLevelInput} levelInput - The log level or log level object
	 * @param {...any} args - The arguments to log
	 * @returns {Function|null} Returns a function if called with no args, null if intercepted
	 * @static
	 */
	static log(levelInput, ...args) {
		if (args.length === 0) {
			// Basically, if someone calls Logger.debug() with no parameters,
			// they get a callback to output a debug message that accepts
			// only 3 args:
			// -message - the string to output
			// -context - Arbitrary object to output to the console (and log)
			// -customContextProps - Arbitrary object NOT to log to the console, but include in JSON/datadog logs
			const fn = (message, context, customContextProps) => {
				const currentProps = levelInput.type
					? levelInput
					: { type: levelInput };
				return this.log(
					{
						...currentProps,
						customContextProps: {
							...currentProps.customContextProps,
							...customContextProps,
						},
					},
					message,
					context,
				);
			};

			// User can also do Logger.info().log(...)
			fn.log = fn;
			return fn;
		}
		// eslint-disable-next-line no-console
		// console.log(`Logger.log debug:`, levelInput, ...args);

		// Updated Nov 2024 to allow multiple filter callbacks
		const { filterCallbacks = [] } = this;

		// Meta data to use for logging
		const {
			level,
			method,
			color,
			customPrefix,
			codeLocationOverride,
			onLogOverride,
		} = Logger.logLevelMeta(levelInput);

		// For use in threads, we intercept log statements and pass to parent
		// so this utility takes the __loggerMeta (built in child thread)
		// and logs that explicitly, then skips the rest of the logic below
		const lastArg = args ? args[args.length - 1] : null;
		if (lastArg && lastArg.__loggerMeta) {
			const { output } = lastArg.__loggerMeta;
			const [
				date,
				pid,
				location,
				// eslint-disable-next-line no-shadow
				customPrefix,
				// eslint-disable-next-line no-unused-vars
				unusedLevel,
				...outputArgs
			] = output;

			// Allow interceptor to grab stuff before hitting console
			const filterArgs = [levelInput, location, pid, ...outputArgs];
			// Call each filter callback until one returns true or we run out
			const intercepted = filterCallbacks.find((cb) => {
				// Callback will BLOCK (intercept) by returning a truthy value
				return !!cb(...filterArgs);
			});

			if (intercepted) {
				return null;
			}

			// We can't just pass .output to console like console.log(...output)
			// because the chalk colors don't come thru from the child thread.
			// Therefore, we have to explicitly extract the fields (above)
			// and re-apply chalk colors to match output from this thread
			safeLog(
				method,
				chalk.grey(date),
				chalk.grey(pid),
				chalk.cyan(location),
				chalk.magenta(customPrefix),
				`[${chalk[color](level)}]`,
				...outputArgs,
			);

			return null;
		}

		// Get location (outside of Logger) where the log originated
		const { file, line } = getCallerLocation();

		// Build short string for file location and line number
		const codeLocation = codeLocationOverride || `${file}:${line}`; // + '@' + caller.getFunctionName(); // TBD do we need function name in logs?
		// const codeLocationShort =
		// 	(codeLocation.length > MAX_LENGTH ? '…' : '') +
		// 	codeLocation.substring(
		// 		codeLocation.length - MAX_LENGTH,
		// 		codeLocation.length,
		// 	);
		// .padStart(MAX_LENGTH, ' ');

		// if (
		// 	filterCallback &&
		// 	!filterCallback(levelInput, codeLocation, process.pid, ...args)
		// ) {
		// 	return null;
		// }

		// Allow interceptor to grab stuff before hitting console
		const filterArgs = [levelInput, codeLocation, process.pid, ...args];
		// Call each filter callback until one returns true or we run out
		const intercepted = filterCallbacks.find((cb) => {
			// Callback will BLOCK (intercept) by returning a truthy value
			return !!cb(...filterArgs);
		});

		if (intercepted) {
			return null;
		}

		// Build output - in the future, could send to cloud or Sentry, etc
		const output = [
			chalk.grey(`(PID ${process.pid})`),
			chalk.grey(new Date().toISOString()),
			customPrefix ? chalk.magenta(customPrefix) : '',
			// Make all log-levels same string length for nice alignment with padEnd()
			// "[" + chalk[color](level.padEnd(DEBUG.length, ' ')) + "]",
			`[${chalk[color](level)}]`,
			chalk.grey(`${codeLocation}:`),
			// // Actual user's log statement
			// ...args,
		];
		const message = args.shift();
		if (message && typeof message === 'string') {
			output.push(chalk[color](message));
		} else {
			output.push(message);
		}

		output.push(...args);

		// Allow interceptor to grab stuff before hitting console
		// Added support for attaching an 'onLog' prop to the filter callback,
		// to allow for more formatted log interception. First added for some of the CLI agent work in the langchain folder.

		const onLogArgs = [
			level,
			codeLocation,
			...args,
			{
				__loggerMeta: {
					output,
				},
			},
		];

		const filterStack = [
			onLogOverride,
			{ onLog: this.onLog },
			...filterCallbacks,
		];

		// // eslint-disable-next-line no-console
		// console.log(` -- filterStack`, filterStack);

		// Call each filter callback until one returns true or we run out
		const interceptedOnLog = filterStack.find((cb) => {
			if (cb && cb.onLog && typeof cb.onLog === 'function') {
				const fn = cb.onLog;
				// Callback will BLOCK (intercept) by returning a truthy value
				const result = !!fn(...onLogArgs);
				// // eslint-disable-next-line no-console
				// console.log(` -- cb.onLog result`, [fn, result]);
				return result;
			}
			return false;
		});

		if (interceptedOnLog) {
			return null;
		}

		// For now, just dump to console
		return safeLog(method, ...output);
	}

	/**
	 * Logs a multiline string by splitting it and logging each line separately
	 * @param {LogLevelInput} level - The log level to use
	 * @param {string} string - The multiline string to log
	 * @static
	 */
	static logMultiline(level, string) {
		const lines = string.split('\n');

		// Using .forEach breaks the stackTrace, so use plain loop
		for (let i = 0; i < lines.length; i++) {
			this.log(level, lines[i]);
		}
	}

	/**
	 * Extracts metadata from log level input including console method and color
	 * @param {LogLevelInput} levelInput - The log level input to process
	 * @returns {LogLevelMeta} Metadata object with level, method, color, and other properties
	 * @static
	 */
	static logLevelMeta(levelInput) {
		const {
			type: level,
			customPrefix,
			codeLocationOverride,
			customContextProps,
			customLogger,
			onLogOverride,
		} = levelInput && levelInput.type ? levelInput : { type: levelInput };

		const { Info, Debug, Warn, Error } = LogLevel;

		const method =
			{
				[Error]: 'error',
				[Warn]: 'warn',
				[Info]: 'log',
				[Debug]: 'debug',
			}[level] || 'log';

		const color =
			{
				[Error]: 'red',
				[Warn]: 'yellow',
				[Info]: 'green',
				[Debug]: 'cyan',
			}[level] || 'green';

		return {
			level,
			method,
			color,
			customPrefix,
			codeLocationOverride,
			customContextProps,
			customLogger,
			onLogOverride,
		};
	}

	/**
	 * Logs an error message
	 * @param {...any} args - Arguments to log
	 * @static
	 */
	static error = (...args) => {
		this.log(LogLevel.Error, ...args);
	};

	/**
	 * Logs a warning message
	 * @param {...any} args - Arguments to log
	 * @static
	 */
	static warn = (...args) => {
		this.log(LogLevel.Warn, ...args);
	};

	/**
	 * Logs an info message
	 * @param {...any} args - Arguments to log
	 * @static
	 */
	static info = (...args) => {
		this.log(LogLevel.Info, ...args);
	};

	/**
	 * Logs a debug message
	 * @param {...any} args - Arguments to log
	 * @static
	 */
	static debug = (...args) => {
		this.log(LogLevel.Debug, ...args);
	};

	/**
	 * Short alias for error logging
	 * @param {...any} args - Arguments to log
	 * @static
	 */
	static e = (...args) => {
		this.log(LogLevel.Error, ...args);
	};

	/**
	 * Short alias for warning logging
	 * @param {...any} args - Arguments to log
	 * @static
	 */
	static w = (...args) => {
		this.log(LogLevel.Warn, ...args);
	};

	/**
	 * Short alias for info logging
	 * @param {...any} args - Arguments to log
	 * @static
	 */
	static i = (...args) => {
		this.log(LogLevel.Info, ...args);
	};

	/**
	 * Short alias for debug logging
	 * @param {...any} args - Arguments to log
	 * @static
	 */
	static d = (...args) => {
		this.log(LogLevel.Debug, ...args);
	};

	/**
	 * Creates a custom logger instance with a specific prefix and context
	 * @param {string} prefix - The prefix to prepend to all log messages
	 * @param {Object} [customContextProps] - Custom context properties to include
	 * @param {OnLogCallback} [onLogOverride] - Override for onLog callback
	 * @returns {CustomLogger} A custom logger instance with prefixed methods
	 * @static
	 */
	static getPrefixedCustomLogger(prefix, customContextProps, onLogOverride) {
		const customPrefix = prefix;
		// const wrap = (type) => (message, ...args) => {
		// 	const string =
		// 		message instanceof Error
		// 			? `${message.message}\n${message.stack}`
		// 			: message;

		// 	return Logger.log(type, `${prefix} ${string}`, ...args);
		// };

		const fakeAuditLog = (actionId, action, comments /* inputProps = {} */) =>
			Logger.warn(`[NO AUDIT LOGGER]`, action, comments, {
				noAuditLogger: true,
			});

		const {
			auditLog,
			authorization,
			customContextProps: currentLoggerCustomContextProps,
		} = this;

		const customLogger = {};

		const wrap =
			(type) =>
			(...args) => {
				return Logger.log(
					{
						type,
						customPrefix,
						customContextProps: customLogger.customContextProps,
						customLogger,
						onLogOverride,
					},
					...args,
				);
			};

		Object.assign(customLogger, {
			log: (level, ...data) => {
				const logLevel = level;
				if (logLevel.type) {
					// Merge with props from customLogger
					logLevel.customContextProps = {
						...customLogger.customContextProps,
						...logLevel.customContextProps,
					};

					if (!logLevel.customPrefix) {
						logLevel.customPrefix = customPrefix;
					}

					if (!logLevel.customLogger) {
						logLevel.customLogger = customLogger;
					}

					if (!logLevel.onLogOverride) {
						logLevel.onLogOverride = onLogOverride;
					}
				}

				return Logger.log(logLevel, ...data);
			},
			error: wrap(LogLevel.Error),
			warn: wrap(LogLevel.Warn),
			info: wrap(LogLevel.Info),
			debug: wrap(LogLevel.Debug),
			customPrefix,
			// Can set persistent props here...
			customContextProps: {
				...currentLoggerCustomContextProps,
				...customContextProps,
			},
			onLogOverride,
			// For app-specific props for AuditLog
			auditLog: auditLog || fakeAuditLog,
			authorization,

			getPrefixedCustomLogger: (
				prefixSuffix,
				extraCustomProps,
				newOnLogOverride,
			) => {
				return Logger.getPrefixedCustomLogger(
					`${customPrefix}${prefixSuffix}`,
					{ ...customContextProps, ...extraCustomProps },
					// Fall back to parent's filter if none provided
					newOnLogOverride ?? onLogOverride,
				);
			},

			alert: (label, ...args) => {
				if (Logger.onAlert) {
					if (
						Logger.onAlert(label, ...args, {
							logger: customLogger,
							authorization,
							customContextProps: currentLoggerCustomContextProps,
						})
					) {
						return;
					}
				}

				Logger.warn(`Unhandled ALERT: ${label}`, ...args);
			},
		});

		// This keeps us from having to do this on every call in the app...
		if (auditLog) {
			auditLog.logger = customLogger;
		}

		return customLogger;
	}

	/**
	 * Sends an alert notification, using the configured alert handler if available
	 * @param {string} label - The alert label/identifier
	 * @param {...any} args - Additional arguments for the alert
	 * @static
	 */
	static alert(label, ...args) {
		if (this.onAlert) {
			if (this.onAlert(label, ...args)) {
				return;
			}
		}

		Logger.warn(`Unhandled ALERT: ${label}`, ...args);
	}

	/**
	 * Default audit log implementation that logs to console when no audit logger is configured
	 * @param {string} actionId - Unique identifier for the action
	 * @param {string} action - Description of the action
	 * @param {string} comments - Additional comments about the action
	 * @returns {Object} Object with id property containing 'console_log'
	 * @static
	 */
	static auditLog(actionId, action, comments /* inputProps = {} */) {
		Logger.warn(
			`No Audit Logger configured on logger instance, logging straight to logs`,
		);
		// Not logging inputProps because they likely are complex this is just a backup
		Logger.info(`[${actionId}] ${action}: ${comments}`);
		return { id: 'console_log' };
	}
}

export default Logger;
export { Logger };

Logger.LogLevel = LogLevel;

/**
 * Filter function that throws errors instead of logging them, useful for scripts/simulations
 * @param {LogLevelInput} levelInput - The log level input
 * @param {string} location - The code location where the log originated
 * @param {number} pid - The process ID
 * @param {string|Error} message - The log message or error object
 * @returns {boolean} Always returns true to intercept the log
 * @throws {Error} Throws an error if the log level is ERROR
 */
export const throwOnErrorFilter = (
	levelInput,
	location,
	pid,
	message,
	// ...args
) => {
	const {
		level,
		// method,
		// color,
		customPrefix,
		// location already overriden by the time the filter gets it
		// codeLocationOverride,
		// customContextProps,
		// customLogger,
	} = Logger.logLevelMeta(levelInput);

	// Make a textual log string for both the TODO log and the api.log
	const logString = [
		pid, // include pid to distinguish easily between restarts
		new Date().toISOString(),
		customPrefix || '',
		`[${level}]`,
		location,
		message instanceof Error ? `${message.message}\n${message.stack}` : message,
		'\n',
	].join(' ');

	if (level === Logger.LogLevel.Error) {
		throw new Error(logString);
	}

	return true;
};

/**
 * Pre-configured script logger with the script filename as prefix
 * Automatically extracts the script name from process.argv[1] for convenient script logging
 * @type {CustomLogger}
 */
export const scriptLogger =
	// Create logger with something like 'create-user.js' as the prefix
	Logger.getPrefixedCustomLogger(
		global.process
			? (global.process.argv[1] && global.process.argv[1].split('/').pop()) ||
					''
			: '?',
	);
